/* eslint-disable @typescript-eslint/no-inferrable-types */
const DeliveryTypes = [
  'upload',
  'private',
  'authenticated',
  'list',
  'fetch',
  'facebook',
  'twitter',
  'twitter_name',
  'gravatar',
  'youtube',
  'hulu',
  'vimeo',
  'animoto',
  'worldstarhiphop',
  'dailymotion',
  'multi',
  'text',
  'asset',
] as const;
type DeliveryType = (typeof DeliveryTypes)[number];
const Transformations = [
  'a',
  'ac',
  'af',
  'ar',
  'b',
  'bo',
  'br',
  'c',
  'co',
  'cs',
  'd',
  'dl',
  'dn',
  'dpr',
  'du',
  'e',
  'eo',
  'f',
  'fl',
  'fn',
  'fps',
  'g',
  'h',
  'if',
  'ki',
  'l',
  'o',
  'p',
  'pg',
  'q',
  'r',
  'so',
  'sp',
  't',
  'u',
  'vc',
  'vs',
  'w',
  'x',
  'y',
  'z',
] as const;
type TransformationPrefix = (typeof Transformations)[number];
type VariableTransformation = `$${string}_${string}`;
type Transformation =
  | `${TransformationPrefix}_${string}`
  | VariableTransformation;
const AssetTypes = ['image', 'video', 'raw'] as const;
type AssetType = (typeof AssetTypes)[number];

/**
 * A utility class to parse and edit Cloudinary URLs
 * @see https://cloudinary.com/documentation/image_transformations#transformation_url_syntax
 */
export class ParsedCloudinaryURL {
  /**
   * Whether or not to use `https`
   */
  public static USE_SSL = true;
  /**
   * Root host of the cloudinary URLs
   */
  public static CLOUDINARY_ROOT = 'res.cloudinary.com';
  /**
   * Regex to find cloudinary transformations within the transformations part of the URL
   */
  public static readonly TRANSFORMATION_REGEX = new RegExp(
    `((?:${Transformations.join('|')})_[^,]+?)(?:,|/|$)`,
    'gim'
  );
  /**
   * Regex to find the version part of a cloudinary URL
   */
  public static readonly VERSION_REGEX = /^v\d+/;
  /**
   * Regex to find the extension part of a cloudinary URL
   */
  public static readonly EXTENSION_REGEX = /\.\w+$/m;

  /**
   * Helper method to test if a URL can be parsed as a cloudinary URL
   * @param url the url to test
   * @returns true if it can be parsed as a cloudinary url, false otherwise
   */
  public static isCloudinaryURL(url: string): boolean {
    try {
      new ParsedCloudinaryURL(url);
      return true;
    } catch {
      return false;
    }
  }
  /**
   * A unique public identifier for the cloudinary environment, used for URL building and API access.
   */
  public CloudName: string;
  /**
   * The type of asset
   */
  public AssetType: AssetType;
  /**
   * The storage or delivery type
   */
  public DeliveryType: DeliveryType;
  /**
   * Cloudinary transformations to apply to the requested image
   */
  public Transformations: Transformation[] = [];
  /**
   * Optional. The version in your delivery URL to bypass the cached version on the CDN
   * and force delivery of the latest asset
   * (in the case that an asset has been overwritten with a newer file).
   */
  public Version = '';
  /**
   * The unique identifier of the asset, including the folder structure if relevant.
   */
  public PublicID: string;
  /**
   * Optional. The file extension of the requested delivery format for the asset.
   * Default: The originally uploaded format or the format determined by f_auto, when used.
   */
  public Extension = '';

  /**
   * Query part of the URL
   */
  public Query = '';
  /**
   * Parse a cloudinary URL and turn it into a ParsedCloudinaryURL object
   * @param url the cloudinary URL to parse
   * @param addDefaultVersion whether or not to add the default version ("v1") if the version component is missing
   */
  public constructor(url: string, addDefaultVersion = false) {
    if (!url.includes(ParsedCloudinaryURL.CLOUDINARY_ROOT)) {
      throw new Error('Not a cloudinary URL');
    }
    const urlObj = new URL(url);
    this.Query = urlObj.search;
    const split = urlObj.pathname.toLowerCase().split('/');

    // Parse cloudname
    this.CloudName = split[1];
    if (!AssetTypes.includes(split[2] as AssetType)) {
      throw new Error('Invalid asset type');
    }

    // Parse asset type
    this.AssetType = split[2] as AssetType;
    if (!DeliveryTypes.includes(split[3] as DeliveryType)) {
      throw new Error('Invalid delivery type');
    }

    // Parse delivery type
    this.DeliveryType = split[3] as DeliveryType;

    // Parse transformations
    let currentURLPartIndex = 4;
    for (; currentURLPartIndex < split.length; currentURLPartIndex++) {
      const urlPart = split[currentURLPartIndex];
      const matches = Array.from(
        urlPart.matchAll(ParsedCloudinaryURL.TRANSFORMATION_REGEX)
      );
      if (matches.length === 0) break;
      matches.forEach((m) => {
        this.AddTransformation(m[1].trim() as Transformation);
      });
    }

    // Parse version
    if (addDefaultVersion) this.Version = 'v1';
    if (ParsedCloudinaryURL.VERSION_REGEX.test(split[currentURLPartIndex])) {
      this.Version = split[currentURLPartIndex];
      currentURLPartIndex++;
    }

    // Parse public ID
    this.PublicID = split.slice(currentURLPartIndex).join('/');

    const extensionMatch = this.PublicID.match(
      ParsedCloudinaryURL.EXTENSION_REGEX
    );

    // Parse extension
    this.Extension = extensionMatch?.[0].replace('.', '') ?? '';
    if (this.Extension === '') return;
    this.PublicID = this.PublicID.substring(0, extensionMatch?.index);
  }

  /**
   * Add a cloudinary transformation to the transformation part of the URL
   * @param transformation the transformation to add
   */
  public AddTransformation(transformation: Transformation) {
    // Add the new transformation
    this.Transformations.push(transformation);

    // De-duplicate the transformations array
    this.Transformations = Array.from(new Set(this.Transformations));
  }
  /**
   * Remove a cloudinary transformation from the transformation part of the URL
   * @param transformation the transformation to remove
   * @returns a boolean indicating if the element was removed or not
   */
  public RemoveTransformation(
    transformation: TransformationPrefix | Transformation
  ): number {
    const removed = this.Transformations.filter(
      (t) => !t.startsWith(transformation)
    );
    const removedCount = this.Transformations.length - removed.length;
    // De-duplicate the transformations array
    this.Transformations = Array.from(new Set(removed));
    return removedCount;
  }
  /**
   * Get the value for a particular transformation in the URL
   * @param transformation the transformation prefix to look up
   * @returns the value for that transformation, or null if it doesn't exist
   */
  public GetTransformationValue(
    transformation: TransformationPrefix
  ): string | null {
    return (
      this.Transformations.find((t) => t.startsWith(transformation))?.split(
        '_'
      )?.[1] ?? null
    );
  }
  /**
   * Check if a transformation exists in this URL
   * @param transformation the transformation to search for
   * @returns whether or not this transformation exists
   */
  public HasTransformation(
    transformation: TransformationPrefix | Transformation
  ): boolean {
    return (
      this.Transformations.find((t) => t.startsWith(transformation)) !==
      undefined
    );
  }
  /**
   * Get the cloudinary URL as a string, built from the individual parsed parts
   * @returns a valid cloudinary URL
   */
  public GetURL(): string {
    const protocol = ParsedCloudinaryURL.USE_SSL ? 'https://' : 'http://';
    const transformations = this.Transformations.join(',');
    const ext = this.Extension !== '' ? `.${this.Extension}` : '';
    return (
      protocol +
      `${ParsedCloudinaryURL.CLOUDINARY_ROOT}/${this.CloudName}/${this.AssetType}/${this.DeliveryType}/${transformations}/${this.Version}/${this.PublicID}${ext}${this.Query}`.replaceAll(
        /\/{2,}/g,
        '/'
      )
    );
  }
}
